import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  FiEdit,
  FiTrash2,
  FiPlus,
  FiUpload,
  FiSave,
  FiX,
  FiImage,
} from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ClipLoader } from "react-spinners";
import { createSlug } from "./utils/slugUtils";
import { adminFetch } from "./utils/adminApi";
import "./AdminNewsManagement.css";

const AdminNewsManagement = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form state
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchArticles();
  }, []);

  // Lấy danh sách bài viết từ Firebase
  const fetchArticles = async () => {
    setLoading(true);
    try {
      const result = await adminFetch("/api/admin/news?limit=1000");
      const newsArray = (result.data || []).map((article) => ({
        ...article,
        createdAt: article.createdAt || Date.now(),
        slug: article.slug || createSlug(article.title || ""),
      }));
      setArticles(newsArray);
    } catch (error) {
      console.error("Error fetching articles:", error);
      toast.error("Có lỗi khi tải danh sách bài viết");
    } finally {
      setLoading(false);
    }
  };

  // Upload ảnh lên Cloudinary
  const uploadToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("upload_preset", "AutoWork");
    formData.append("api_key", "238276161954221");

    try {
      const response = await axios.post(
        "https://api.cloudinary.com/v1_1/test-upload-1/image/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      return response.data.secure_url;
    } catch (error) {
      console.error("Failed to upload image to Cloudinary:", error);
      throw error;
    }
  };

  // Xử lý upload ảnh
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setIsUploading(true);
    try {
      const cloudinaryUrl = await uploadToCloudinary(file);
      setImageUrl(cloudinaryUrl);
      toast.success("Upload ảnh thành công!");
    } catch (error) {
      toast.error("Có lỗi khi upload ảnh. Vui lòng thử lại!");
    } finally {
      setIsUploading(false);
    }
  };

  // Mở modal tạo bài viết mới
  const openCreateModal = () => {
    setEditingArticle(null);
    setTitle("");
    setContent("");
    setDescription("");
    setImageUrl("");
    setIsModalOpen(true);
  };

  // Mở modal chỉnh sửa bài viết
  const openEditModal = (article) => {
    setEditingArticle(article);
    setTitle(article.title || "");
    setContent(article.content || "");
    setDescription(article.description || "");
    setImageUrl(article.imageUrl || "");
    setIsModalOpen(true);
  };

  // Đóng modal
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingArticle(null);
    setTitle("");
    setContent("");
    setDescription("");
    setImageUrl("");
  };

  // Lưu bài viết
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      toast.error("Vui lòng nhập đầy đủ tiêu đề và nội dung bài viết!");
      return;
    }

    setIsSubmitting(true);
    try {
      const articleData = {
        title: title.trim(),
        content: content.trim(),
        description:
          description.trim() ||
          content.replace(/<[^>]*>/g, "").substring(0, 160) + "...",
        imageUrl: imageUrl || null,
        slug: createSlug(title.trim()),
        createdAt: editingArticle ? editingArticle.createdAt : Date.now(),
        updatedAt: Date.now(),
      };

      if (editingArticle) {
        // Cập nhật bài viết
        await adminFetch(
          `/api/admin/news/${encodeURIComponent(editingArticle.id)}`,
          {
            method: "PATCH",
            body: JSON.stringify({ articleData }),
          }
        );
        toast.success("Cập nhật bài viết thành công!");
      } else {
        // Tạo bài viết mới
        await adminFetch("/api/admin/news", {
          method: "POST",
          body: JSON.stringify({ articleData }),
        });
        toast.success("Tạo bài viết mới thành công!");
      }

      closeModal();
      fetchArticles(); // Reload danh sách
    } catch (error) {
      console.error("Error saving article:", error);
      toast.error("Có lỗi khi lưu bài viết. Vui lòng thử lại!");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Xóa bài viết
  const handleDelete = async (articleId) => {
    if (!window.confirm("Bạn có chắc chắn muốn xóa bài viết này?")) {
      return;
    }

    try {
      await adminFetch(`/api/admin/news/${encodeURIComponent(articleId)}`, {
        method: "DELETE",
      });
      toast.success("Xóa bài viết thành công!");
      fetchArticles(); // Reload danh sách
    } catch (error) {
      console.error("Error deleting article:", error);
      toast.error("Có lỗi khi xóa bài viết. Vui lòng thử lại!");
    }
  };

  // Định dạng ngày tháng
  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString("vi-VN", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <ClipLoader size={30} color="#3B82F6" />
        <span className="ml-2">Đang tải...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-semibold text-gray-800">
          Quản lý tin tức
        </h2>
        <button
          onClick={openCreateModal}
          className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiPlus size={20} />
          Tạo bài viết mới
        </button>
      </div>

      {/* Danh sách bài viết */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="p-6 border-b">
          <h3 className="text-lg font-medium text-gray-900">
            Danh sách bài viết ({articles.length})
          </h3>
        </div>

        <div className="p-6">
          {articles.length === 0 ? (
            <div className="text-center py-8">
              <FiImage size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">Chưa có bài viết nào</p>
              <button
                onClick={openCreateModal}
                className="mt-4 text-blue-600 hover:text-blue-700 font-medium"
              >
                Tạo bài viết đầu tiên
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {articles.map((article) => (
                <div
                  key={article.id}
                  className="border rounded-lg p-4 hover:shadow-sm transition-shadow"
                >
                  <div className="flex gap-4">
                    {/* Ảnh đại diện */}
                    <div className="flex-shrink-0">
                      {article.imageUrl ? (
                        <img
                          src={article.imageUrl}
                          alt={article.title}
                          className="w-20 h-20 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-20 h-20 bg-gray-200 rounded-lg flex items-center justify-center">
                          <FiImage size={24} className="text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Nội dung bài viết */}
                    <div className="flex-grow min-w-0">
                      <div className="flex justify-between items-start">
                        <div className="flex-grow">
                          <h3 className="text-lg font-medium text-gray-900 truncate">
                            {article.title}
                          </h3>
                          <p className="text-sm text-gray-500 mt-1">
                            {article.description}
                          </p>
                          <p className="text-xs text-gray-400 mt-2">
                            Tạo: {formatDate(article.createdAt)}
                            {article.updatedAt &&
                              article.updatedAt !== article.createdAt &&
                              ` • Cập nhật: ${formatDate(article.updatedAt)}`}
                          </p>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 ml-4">
                          <button
                            onClick={() => openEditModal(article)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Chỉnh sửa"
                          >
                            <FiEdit size={18} />
                          </button>
                          <button
                            onClick={() => handleDelete(article.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Xóa"
                          >
                            <FiTrash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal tạo/chỉnh sửa bài viết */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingArticle ? "Chỉnh sửa bài viết" : "Tạo bài viết mới"}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX size={24} />
              </button>
            </div>

            {/* Modal Body */}
            <form
              onSubmit={handleSubmit}
              className="p-6 space-y-6 overflow-y-auto max-h-[calc(90vh-140px)]"
            >
              {/* Tiêu đề */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tiêu đề bài viết *
                </label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Nhập tiêu đề bài viết..."
                  required
                />
              </div>

              {/* Mô tả ngắn */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mô tả ngắn
                  <span className="text-gray-500 text-xs ml-1">
                    (tự động tạo nếu để trống)
                  </span>
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Mô tả ngắn về bài viết..."
                  rows={3}
                />
              </div>

              {/* Nội dung HTML */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nội dung bài viết (HTML) *
                </label>
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                  placeholder="<p>Nội dung bài viết...</p>"
                  rows={15}
                  required
                />
              </div>

              {/* Ảnh đại diện */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ảnh đại diện
                </label>
                <div className="flex gap-4 items-center">
                  <input
                    type="text"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    className="flex-grow px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="URL ảnh hoặc upload ảnh..."
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    className="flex items-center gap-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
                  >
                    {isUploading ? (
                      <ClipLoader size={16} color="#6B7280" />
                    ) : (
                      <FiUpload size={16} />
                    )}
                    Upload
                  </button>
                </div>

                {/* Preview ảnh */}
                {imageUrl && (
                  <div className="mt-2">
                    <img
                      src={imageUrl}
                      alt="Preview"
                      className="w-32 h-24 object-cover rounded border"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Hủy
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <ClipLoader size={16} color="#FFFFFF" />
                  ) : (
                    <FiSave size={16} />
                  )}
                  {editingArticle ? "Cập nhật" : "Tạo bài viết"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer />
    </div>
  );
};

export default AdminNewsManagement;
